import { forwardRef, lazy, useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Debounce } from '@medifind/hooks';
import { useLazyComponentPreload } from '@medifind/hooks';
import { IconArrowSmall } from '@medifind/icons';
import { isPrerender } from '@medifind/utils';
import { eventDispatch } from '@medifind/utils';
import { EventListener } from '@medifind/utils';
import { useMediaQueryUp } from '@medifind/utils';
import { addBodyClass, hasBodyClass, removeBodyClass } from '@medifind/utils';
import styles from './displayConditionPopoverHandler.module.scss';
const scrollDebounce = new Debounce(100, { runAtEnd: true }).predebounce;
const ConditionSearchPopover = lazy(() => import('../../components/menu/ConditionSearchPopover'));
const Button = lazy(() => import('@medifind/lazy-loadable/lib/shared-basic-components/inputs/LazyButton'));

const DisplayConditionPopoverHandler = ({ specialty, role, /* conditionList, */ codingList, popupButton, popoverAt = 'md' }) => {
  const isLargeDevice = useMediaQueryUp(popoverAt);
  const [isConditionDisplayed, setIsConditionDisplayed] = useState(isLargeDevice);
  const [showConditionOnClick, setShowConditionOnClick] = useState(false);
  const [showConditionOnScroll, setShowConditionOnScroll] = useState(false);
  useLazyComponentPreload([ConditionSearchPopover], 5000);
  useEffect(() => {
    if (!isLargeDevice) {
      const handleScroll = () => {
        scrollDebounce(() => {
          if (popoverAt && popupButton?.current) {
            const refineBtnTop = popupButton.current.getBoundingClientRect().top;
            if (refineBtnTop < 0) {
              setShowConditionOnScroll(true);
              setIsConditionDisplayed(true);
            } else {
              setShowConditionOnScroll(false);
              setIsConditionDisplayed(false);
            }
          }
        });
      };
      window.addEventListener('scroll', handleScroll, false);

      return () => {
        window.removeEventListener('scroll', handleScroll, false);
      };
    }
  }, [popoverAt, isLargeDevice]);

  useEffect(() => {
    if (popoverAt && popupButton?.current) {
      const refineBtnTop = popupButton.current.getBoundingClientRect().top;
      if (isLargeDevice) {
        setShowConditionOnClick(false);
        setShowConditionOnScroll(false);
        setIsConditionDisplayed(false);
      } else {
        if (!isConditionDisplayed && refineBtnTop < 0) {
          setShowConditionOnScroll(true);
          setIsConditionDisplayed(true);
        }
      }
    }
  }, [popoverAt, isLargeDevice, isConditionDisplayed]);

  useEffect(() => {
    if (popoverAt) {
      if (showConditionOnClick) {
        if (!hasBodyClass(styles['body--overflow-hidden'])) {
          addBodyClass(styles['body--overflow-hidden']);
          addBodyClass(styles[`popoverAt--${popoverAt}`]);
        }
      } else {
        if (hasBodyClass(styles['body--overflow-hidden'])) {
          removeBodyClass(styles['body--overflow-hidden']);
          removeBodyClass(styles[`popoverAt--${popoverAt}`]);
        }
      }
      return () => {
        removeBodyClass(styles['body--overflow-hidden']);
        removeBodyClass(styles[`popoverAt--${popoverAt}`]);
      };
    }
  }, [popoverAt, showConditionOnClick]);

  return (
    <>
      <EventListener eventName="conditionSearchPopover--click-open" onEvent={() => setShowConditionOnClick(true)} />
      <EventListener eventName="conditionSearchPopover--click-close" onEvent={() => setShowConditionOnClick(false)} />

      {(isPrerender || isLargeDevice || showConditionOnClick || showConditionOnScroll) && (
        <ConditionSearchPopover
          specialty={specialty}
          title={role ? `Common Conditions Treated by ${role}s` : `Common Conditions`}
          codingList={codingList}
          popoverAt={popoverAt}
          showConditionOnClick={showConditionOnClick}
          showConditionOnScroll={showConditionOnScroll}
        />
      )}
    </>
  );
};

export const ConditionSearchPopoverButton = forwardRef(({ label, screenReaderText, className }, ref) => (
  <Button
    id="condition-search-popover-button"
    classes={{ container: classNames(className) }}
    onClick={() => eventDispatch('conditionSearchPopover--click-open')}
    screenReaderText={screenReaderText}
    label={`${label}`}
    ref={ref}
    color="blue"
    icon={<IconArrowSmall width={32} height={32} color={'white'} />}
    focus="outline"
  />
));
ConditionSearchPopoverButton.displayName = 'ConditionSearchPopoverButton';

DisplayConditionPopoverHandler.propTypes = {
  specialty: PropTypes.string,
  role: PropTypes.string,
  codingList: PropTypes.array,
  popupButton: PropTypes.any,
  popoverAt: PropTypes.string,
};

export default DisplayConditionPopoverHandler;
